import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

// Helper to add ordinal suffix to day number
const ordinal = (day) => {
  const suffix = day === 1 || day === 21 || day === 31 ? 'st' :
                 day === 2 || day === 22 ? 'nd' :
                 day === 3 || day === 23 ? 'rd' : 'th';
  return `${day}${suffix}`;
};

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June',
                     'July', 'August', 'September', 'October', 'November', 'December'];

// Format recurring date value (day of month or MM-DD) with a prefix
const formatRecurringDate = (value, cycle, prefix) => {
  if (!value) return '';
  if (cycle === 'monthly') {
    const day = parseInt(value, 10);
    if (isNaN(day)) return `${prefix}: ${value}`;
    return `${prefix}: ${ordinal(day)} of every month`;
  }
  if (cycle === 'yearly') {
    const parts = value.split('-');
    if (parts.length === 2) {
      const month = parseInt(parts[0], 10);
      const day = parseInt(parts[1], 10);
      if (month >= 1 && month <= 12 && !isNaN(day)) {
        return `${prefix}: ${MONTH_NAMES[month - 1]} ${ordinal(day)}`;
      }
    }
    return `${prefix}: ${value}`;
  }
  return `${prefix}: ${value}`;
};

// Rendered both as its own page (/task-settings, for the company the admin is
// currently working in) and embedded in the admin dashboard's Task Management tab,
// where a company is picked from a drop-down. Embedded, it drops the page chrome —
// the heading and the links out to the todolist and documents — because those
// belong to the surrounding page.
const TaskSettings = ({ userInfo, embedded = false, companyLabel = '' }) => {
  const navigate = useNavigate();
  const [customTasks, setCustomTasks] = useState([]);
  const [customTaskCompletions, setCustomTaskCompletions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTask, setEditingTask] = useState(null);

  // Create task form
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    isRepeated: false,
    repeatDate: '',
    cycle: 'monthly',
    assignedTo: 'admin',
    taskType: 'mark-as-complete',
    fileUploadItems: [],
    dueDate: '',
  });
  const [newFileUploadItem, setNewFileUploadItem] = useState('');

  // Monthly cycle settings state
  const [monthlyCycleSettings, setMonthlyCycleSettings] = useState({
    cycleStartMonth: null,
    isPaused: false
  });
  const [selectedStartYear, setSelectedStartYear] = useState(new Date().getFullYear());
  const [selectedStartMonth, setSelectedStartMonth] = useState((new Date().getMonth() + 1).toString().padStart(2, '0'));
  const [savingCycleSettings, setSavingCycleSettings] = useState(false);

  const monthNames = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' }
  ];

  const getAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    return [currentYear, currentYear - 1, currentYear - 2];
  };
  const availableYears = getAvailableYears();

  // Email template section
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);

  useEffect(() => {
    fetchCustomTasks();
    fetchMonthlyCycleSettings();
    fetchEmailTemplates();
  }, []);

  const fetchCustomTasks = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks`
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTasks(data.tasks || []);
        setCustomTaskCompletions(data.completions || []);
      }
    } catch (error) {
      console.error('Error fetching custom tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyCycleSettings = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        { method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' } }
      );
      if (response.ok) {
        const data = await response.json();
        setMonthlyCycleSettings(data);
        if (data.cycleStartMonth) {
          const [year, month] = data.cycleStartMonth.split('-');
          setSelectedStartYear(parseInt(year));
          setSelectedStartMonth(month);
        }
      }
    } catch (error) {
      console.error('Error fetching monthly cycle settings:', error);
    }
  };

  const fetchEmailTemplates = async () => {
    try {
      const response = await api.get(
        `${process.env.REACT_APP_BACKEND_API_URL}/email-templates`
      );
      if (response.ok) {
        const data = await response.json();
        // Filter to task-related templates
        const taskTemplates = (data.templates || data || []).filter(t =>
          t.name && (t.name.includes('task') || t.name.includes('Task') ||
          t.name.includes('delaware') || t.name.includes('Delaware') ||
          t.name.includes('registered_agent'))
        );
        setEmailTemplates(taskTemplates);
      }
    } catch (error) {
      console.error('Error fetching email templates:', error);
    }
  };

  const handleSaveMonthlyCycleSettings = async () => {
    setSavingCycleSettings(true);
    try {
      const cycleStartMonth = `${selectedStartYear}-${selectedStartMonth}`;
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cycleStartMonth, isPaused: monthlyCycleSettings.isPaused })
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMonthlyCycleSettings(data.settings);
          alert('Monthly cycle settings saved successfully!');
        }
      }
    } catch (error) {
      console.error('Error saving monthly cycle settings:', error);
      alert('Failed to save monthly cycle settings.');
    } finally {
      setSavingCycleSettings(false);
    }
  };

  const handleTogglePause = async () => {
    setSavingCycleSettings(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_API_URL}/month-status/monthly-cycle-settings`,
        {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ isPaused: !monthlyCycleSettings.isPaused })
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setMonthlyCycleSettings(data.settings);
          alert(`Monthly cycle ${data.settings.isPaused ? 'paused' : 'resumed'} successfully!`);
        }
      }
    } catch (error) {
      console.error('Error toggling monthly cycle pause:', error);
    } finally {
      setSavingCycleSettings(false);
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title) {
      alert('Task title is required');
      return;
    }
    try {
      const response = await api.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks`,
        {
          ...newTask,
          fileUploadItems: JSON.stringify(newTask.fileUploadItems),
        }
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTasks(prev => [...prev, data.task]);
        setShowCreateModal(false);
        setNewTask({
          title: '', description: '', isRepeated: false, repeatDate: '',
          cycle: 'monthly', assignedTo: 'admin', taskType: 'mark-as-complete', fileUploadItems: [],
          dueDate: '',
        });
        alert('Task created successfully');
      } else {
        alert('Failed to create task');
      }
    } catch (error) {
      console.error('Error creating task:', error);
      alert('Failed to create task');
    }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      const response = await api.put(
        `${process.env.REACT_APP_BACKEND_API_URL}/admin-tasks/${taskId}`,
        updates
      );
      if (response.ok) {
        const data = await response.json();
        setCustomTasks(prev => prev.map(t => t.id === taskId ? data.task : t));
        if (editingTask?.id === taskId) setEditingTask(null);
        if (data.detachedFromBulkTask) {
          alert('Saved. This task is now a copy for this company only — it has been removed from the bulk task, so future bulk edits will not change it.');
        }
      } else {
        const data = await response.json().catch(() => ({}));
        alert(data.error || 'Failed to update task');
      }
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Failed to update task');
    }
  };

  const activeTasks = customTasks.filter(t => t.status === 'active');
  const pausedTasks = customTasks.filter(t => t.status === 'paused');

  if (!userInfo || !['admin', 'superadmin'].includes(userInfo.accountType)) {
    return (
      <div className={`flex items-center justify-center py-12 px-4 ${embedded ? '' : 'min-h-screen bg-gray-50'}`}>
        <div className="card p-6 max-w-md mx-auto">
          <p className="text-center text-gray-600">This page is only available for administrators.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center ${embedded ? 'py-16' : 'min-h-screen bg-gray-50'}`}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={embedded ? '' : 'max-w-5xl mx-auto p-6'}>
      {!embedded && (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Task Settings</h1>
            <p className="text-gray-600 mt-1">Manage recurring tasks and custom task settings</p>
          </div>
          <button
            onClick={() => navigate('/admin/todolist')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
          >
            Back to Todolist
          </button>
        </div>
      )}

      {embedded && companyLabel && (
        <p className="text-sm text-gray-600 mb-4">
          Showing the task settings for <span className="font-semibold">{companyLabel}</span>.
        </p>
      )}

      {/* Task Settings */}
      <div className="card p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Task Settings</h2>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/task-documents')}
              className="px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              View Documents
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              + Create Task
            </button>
          </div>
        </div>

        {/* Active Tasks */}
        <div className="space-y-3">
          {activeTasks.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No active tasks.</p>
          ) : (
            activeTasks.map(task => (
              <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                {editingTask?.id === task.id ? (
                  // Edit form
                  <div className="space-y-3">
                    {task.isDefault === 'true' && (
                      <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                        Default tasks have limited editing options. Only cycle, due date, and day of month can be modified.
                      </div>
                    )}
                    {task.bulkTaskId && (
                      <div className="p-2 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-700">
                        This task came from a bulk send. Saving turns it into a copy for this
                        company only: the company drops off the bulk task, and later bulk edits
                        will no longer change it.
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
                        <input
                          type="text"
                          value={editingTask.title}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, title: e.target.value }))}
                          className={`w-full border border-gray-300 rounded px-2 py-1 text-sm ${task.isDefault === 'true' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          disabled={task.isDefault === 'true'}
                          readOnly={task.isDefault === 'true'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
                        <select
                          value={editingTask.assignedTo}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, assignedTo: e.target.value }))}
                          className={`w-full border border-gray-300 rounded px-2 py-1 text-sm ${task.isDefault === 'true' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          disabled={task.isDefault === 'true'}
                        >
                          <option value="admin">Admin</option>
                          <option value="client">Client</option>
                          <option value="both">Both</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                      <textarea
                        value={editingTask.description}
                        onChange={(e) => setEditingTask(prev => ({ ...prev, description: e.target.value }))}
                        className={`w-full border border-gray-300 rounded px-2 py-1 text-sm ${task.isDefault === 'true' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                        rows="2"
                        disabled={task.isDefault === 'true'}
                        readOnly={task.isDefault === 'true'}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cycle</label>
                        <select
                          value={editingTask.cycle}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, cycle: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        >
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {editingTask.cycle === 'yearly' ? 'Appears on Date (MM-DD)' : 'Appears on Day of Month'}
                        </label>
                        <input
                          type="text"
                          value={editingTask.repeatDate}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, repeatDate: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          placeholder={editingTask.cycle === 'yearly' ? 'e.g. 03-01' : 'e.g. 1'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Task Type</label>
                        <select
                          value={editingTask.taskType}
                          onChange={(e) => setEditingTask(prev => ({ ...prev, taskType: e.target.value }))}
                          className={`w-full border border-gray-300 rounded px-2 py-1 text-sm ${task.isDefault === 'true' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          disabled={task.isDefault === 'true'}
                        >
                          <option value="mark-as-complete">Mark as Complete</option>
                          <option value="file-upload">File Upload</option>
                          <option value="special">Special (integrated)</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      {editingTask.isRepeated === 'true' || editingTask.isRepeated === true ? (
                        <>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            {editingTask.cycle === 'yearly' ? 'Due Date (MM-DD, optional)' : 'Due Day of Month (optional)'}
                          </label>
                          <input
                            type="text"
                            value={editingTask.dueDate || ''}
                            onChange={(e) => setEditingTask(prev => ({ ...prev, dueDate: e.target.value }))}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder={editingTask.cycle === 'yearly' ? 'e.g. 03-15' : 'e.g. 20'}
                          />
                        </>
                      ) : (
                        <>
                          <label className="block text-xs font-medium text-gray-600 mb-1">Due Date (optional)</label>
                          <input
                            type="date"
                            value={editingTask.dueDate || ''}
                            onChange={(e) => setEditingTask(prev => ({ ...prev, dueDate: e.target.value }))}
                            className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                          />
                        </>
                      )}
                    </div>
                    {/* File Upload Items Editor */}
                    {editingTask.taskType === 'file-upload' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">File Upload Items</label>
                        {(() => {
                          let items = [];
                          try {
                            items = typeof editingTask.fileUploadItems === 'string'
                              ? JSON.parse(editingTask.fileUploadItems || '[]')
                              : (editingTask.fileUploadItems || []);
                          } catch (e) { items = []; }
                          return (
                            <>
                              {items.map((item, idx) => (
                                <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                                  <input
                                    type="text"
                                    value={item}
                                    onChange={(e) => {
                                      const updated = [...items];
                                      updated[idx] = e.target.value;
                                      setEditingTask(prev => ({
                                        ...prev,
                                        fileUploadItems: JSON.stringify(updated)
                                      }));
                                    }}
                                    className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
                                  />
                                  <button
                                    onClick={() => {
                                      const updated = items.filter((_, i) => i !== idx);
                                      setEditingTask(prev => ({
                                        ...prev,
                                        fileUploadItems: JSON.stringify(updated)
                                      }));
                                    }}
                                    className="text-red-500 text-xs"
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => {
                                  const updated = [...items, ''];
                                  setEditingTask(prev => ({
                                    ...prev,
                                    fileUploadItems: JSON.stringify(updated)
                                  }));
                                }}
                                className="text-sm text-blue-600 hover:text-blue-700 mt-1"
                              >
                                + Add Upload Item
                              </button>
                            </>
                          );
                        })()}
                      </div>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleUpdateTask(task.id, {
                          ...(task.isDefault !== 'true' ? {
                            title: editingTask.title,
                            description: editingTask.description,
                            assignedTo: editingTask.assignedTo,
                            taskType: editingTask.taskType,
                          } : {}),
                          cycle: editingTask.cycle,
                          repeatDate: editingTask.repeatDate,
                          isRepeated: editingTask.isRepeated,
                          dueDate: editingTask.dueDate || '',
                          fileUploadItems: editingTask.fileUploadItems,
                        })}
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingTask(null)}
                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // Display
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <p className="font-medium text-gray-900">{task.title}</p>
                        {task.isDefault === 'true' && (
                          <span className="px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded-full">Default</span>
                        )}
                        {/* Sent from the admin dashboard's bulk-add tool. Editing it here
                            forks it into a copy for this company only. */}
                        {task.bulkTaskId && (
                          <span
                            className="px-2 py-0.5 text-xs bg-indigo-100 text-indigo-700 rounded-full"
                            title="Added in bulk from the Admin Dashboard. Edit it there to update every company at once; editing it here makes a copy for this company only."
                          >
                            Bulk
                          </span>
                        )}
                        {task.isRepeated === 'true' && (
                          <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                            {task.cycle === 'yearly' ? 'Yearly' : 'Monthly'}
                          </span>
                        )}
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          task.taskType === 'special' ? 'bg-blue-100 text-blue-700' :
                          task.taskType === 'file-upload' ? 'bg-teal-100 text-teal-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {task.taskType === 'special' ? 'Special' : task.taskType === 'file-upload' ? 'File Upload' : 'Mark Complete'}
                        </span>
                      </div>
                      {task.description && <p className="text-sm text-gray-600 mt-1">{task.description}</p>}
                      <p className="text-xs text-gray-500 mt-1">
                        Assigned to: {task.assignedTo}
                        {task.repeatDate && ` | ${formatRecurringDate(task.repeatDate, task.cycle, 'Appears')}`}
                        {task.dueDate && (task.isRepeated === 'true'
                          ? ` | ${formatRecurringDate(task.dueDate, task.cycle, 'Due')}`
                          : ` | Due: ${new Date(task.dueDate + 'T00:00:00').toLocaleDateString()}`)}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => setEditingTask({ ...task })}
                        className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleUpdateTask(task.id, { status: 'paused' })}
                        className="px-3 py-1 text-sm bg-yellow-500 text-white rounded hover:bg-yellow-600"
                      >
                        Pause
                      </button>
                      {task.isDefault !== 'true' && (
                        <button
                          onClick={() => {
                            if (window.confirm('Delete this task?')) {
                              handleUpdateTask(task.id, { status: 'deleted' });
                            }
                          }}
                          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Paused Tasks */}
        {pausedTasks.length > 0 && (
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-500 mb-3">Paused Tasks</h3>
            <div className="space-y-2">
              {pausedTasks.map(task => (
                <div key={task.id} className="border border-gray-200 rounded-lg p-3 opacity-60">
                  <div className="flex justify-between items-center">
                    <p className="font-medium text-gray-700">{task.title}</p>
                    <button
                      onClick={() => handleUpdateTask(task.id, { status: 'active' })}
                      className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Restart
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Task Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Create New Task</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="Task title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  rows="2"
                />
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={newTask.isRepeated}
                    onChange={(e) => setNewTask(prev => ({ ...prev, isRepeated: e.target.checked }))}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-700">Recurring task</span>
                </label>
              </div>
              {newTask.isRepeated && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Cycle</label>
                    <select
                      value={newTask.cycle}
                      onChange={(e) => setNewTask(prev => ({ ...prev, cycle: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {newTask.cycle === 'yearly' ? 'Appears on Date (MM-DD)' : 'Appears on Day of Month'}
                    </label>
                    <input
                      type="text"
                      value={newTask.repeatDate}
                      onChange={(e) => setNewTask(prev => ({ ...prev, repeatDate: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder={newTask.cycle === 'yearly' ? '03-01' : '1'}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {newTask.cycle === 'yearly' ? 'Due Date (MM-DD, optional)' : 'Due Day of Month (optional)'}
                    </label>
                    <input
                      type="text"
                      value={newTask.dueDate}
                      onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                      placeholder={newTask.cycle === 'yearly' ? '03-15' : '20'}
                    />
                  </div>
                </div>
              )}
              {!newTask.isRepeated && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date (optional)</label>
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
                <select
                  value={newTask.assignedTo}
                  onChange={(e) => setNewTask(prev => ({ ...prev, assignedTo: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="admin">Admin</option>
                  <option value="client">Client</option>
                  <option value="both">Both</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Task Type</label>
                <select
                  value={newTask.taskType}
                  onChange={(e) => setNewTask(prev => ({ ...prev, taskType: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="mark-as-complete">Mark as Complete</option>
                  <option value="file-upload">File Upload</option>
                </select>
              </div>
              {newTask.taskType === 'file-upload' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">File Upload Items</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={newFileUploadItem}
                      onChange={(e) => setNewFileUploadItem(e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2"
                      placeholder="e.g. Bank Statement PDF"
                    />
                    <button
                      onClick={() => {
                        if (newFileUploadItem.trim()) {
                          setNewTask(prev => ({
                            ...prev,
                            fileUploadItems: [...prev.fileUploadItems, newFileUploadItem.trim()]
                          }));
                          setNewFileUploadItem('');
                        }
                      }}
                      className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                  {newTask.fileUploadItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                      <span>- {item}</span>
                      <button
                        onClick={() => setNewTask(prev => ({
                          ...prev,
                          fileUploadItems: prev.fileUploadItems.filter((_, i) => i !== idx)
                        }))}
                        className="text-red-500 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex space-x-2 pt-4">
                <button onClick={handleCreateTask} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex-1">
                  Create Task
                </button>
                <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-1">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskSettings;
